import React, { FC, useEffect, useState } from 'react';

import throttle from 'lodash/throttle';
import { Box, ABTestContext } from '@lucid/components-react';
import { mqMax } from '@lucid/theme';

interface ABTestAdminUIProps {
  abTests: typeof ABTestContext;
}

const ABTestAdminUi: FC<ABTestAdminUIProps> = ({ abTests }) => {
  const [collapsed, setCollapsed] = useState(
    typeof window !== 'undefined'
      ? window.localStorage.getItem('abTestAdmin-collapsed') === '1'
      : false
  );
  const [currentY, setCurrentY] = useState(0);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const thisY = window.pageYOffset;
      setCurrentY(thisY);
    }, 200);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleAbtestAdmin = () => {
    window.localStorage.setItem(
      'abTestAdmin-collapsed',
      !collapsed ? '1' : '0'
    );

    setCollapsed(!collapsed);
  };

  const numTests = Object.keys(abTests).length;

  return (
    <Box
      css={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        zIndex: 100000,
        color: 'white',
        transition: 'opacity 0.25s ease, max-height 0.5s ease',
        opacity: collapsed ? 0.2 : 'unset',
        background: '#3A414A',
        padding: '0 1rem',
        overflow: collapsed ? 'hidden' : 'auto',
        maxHeight: collapsed ? '3.25rem' : '40rem',
        boxShadow: '1px 1px 2px 1px black',
        [mqMax.tablet]: {
          display: 'none'
        },
        '&:hover': {
          opacity: 1
        },
        '*': {
          outlineWidth: '2px',
          outlineColor: 'orange'
        }
      }}
    >
      <button
        css={{
          fontSize: '1rem',
          fontWeight: 700,
          margin: '1rem 0',
          background: 'transparent',
          border: 0,
          color: 'white',
          '&:hover': {
            cursor: 'pointer'
          }
        }}
        onClick={() => toggleAbtestAdmin()}
        type="button"
      >
        {`${numTests} AB Test${numTests === 1 ? '' : 's'} on this Page`}
      </button>
      {Object.entries(abTests).map(
        ([
          test,
          { branches, branch, trigger, triggerFired, distance, updateBranch }
        ]) => (
          <Box
            key={test}
            css={{
              margin: '0 0 1rem',
              padding: '0.5rem 0 0',
              borderTop: '1px solid white'
            }}
          >
            <p css={{ margin: 0, padding: 0 }}>{test}</p>
            <p
              css={{
                margin: '0 0 0.5rem',
                padding: 0,
                fontSize: '0.75rem',
                color: triggerFired ? 'yellow' : 'orange'
              }}
            >
              {`${triggerFired ? 'Was assigned' : 'Will assign'} on ${trigger}`}
              {trigger === 'scroll' ? ` (${distance}px)` : ''}
            </p>
            <form>
              {branches &&
                  Object.keys(branches)
                    .filter(curBranch => branches[curBranch] !== null)
                    .map(curBranch => (
                      <label
                        key={`${test}|${curBranch}`}
                        css={{
                          margin: '0 0.5rem',
                          transition: 'font-size 0.25s ease',
                          color: curBranch === branch ? 'white' : '#838A93',
                          fontWeight: curBranch === branch ? 700 : 'unset',
                          '&:hover': {
                            cursor: 'pointer'
                          }
                        }}
                      >
                        <input
                          aria-checked={curBranch === branch}
                          checked={curBranch === branch}
                          css={{
                            margin: 0,
                            padding: 0,
                            height: '15px',
                            marginRight: '0.25rem'
                          }}
                          name={test}
                          onChange={() => {
                            const storageKey = `abTest_${test}`;
                            if (curBranch) {
                              window.localStorage.setItem(storageKey, curBranch);
                            }
                            updateBranch(curBranch);
                          }}
                          type="radio"
                        />
                        {`${curBranch.replace(/_/g, '-')}: ${
                          branches[curBranch]
                        }`}
                      </label>
                    ))}
            </form>
          </Box>
        )
      )}
      <p css={{ fontSize: '0.65rem', textAlign: 'right' }}>
        {`Scroll Depth: ${currentY}`}
      </p>
    </Box>
  );
};

export default ABTestAdminUi;
